import React from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress, Grid2, Paper, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { OpsTatMetricId } from "../../../../../../models/Dashboard/OpsTat/OpsTatDrilldown";
import { OpsTatStageCard } from "../../../../../../models/Dashboard/OpsTat/OpsTatSummary";
import { NewOperationsDashboardStore } from "../../../../stores/dashboards/NewOperationsDashboardStore";
import {
  buildOpsTatSparklineSegments,
  formatOpsTatCount,
  formatOpsTatDays,
  formatOpsTatSignedDays,
  getOpsTatStatusColors,
  getOpsTatStatusPillSx,
  toOpsTatSparklineArea,
  toOpsTatSparklinePath,
} from "./opsTatFormat";
import OpsTatHowToRead from "./OpsTatHowToRead";

interface OpsTatStageGridProps {
  store: NewOperationsDashboardStore;
}

const SLA_BAR_CAP = 1.6;

const OpsTatInlineState = ({ message }: { message: string }) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="10rem"
      textAlign="center"
      border="1px dashed"
      borderColor="divider"
      borderRadius={2}
      px={2}
    >
      <CustomTypography
        variant="heading6"
        fontWeight={500}
        color={theme.palette.customColors.greyTextColor}
      >
        {message}
      </CustomTypography>
    </Box>
  );
};

const StageSparkline = ({
  trend,
  color,
}: {
  trend: Array<number | null>;
  color: string;
}) => {
  const width = 200;
  const height = 26;

  try {
    const { segments, lastPoint } = buildOpsTatSparklineSegments(
      trend,
      width,
      height,
    );

    if (segments.length === 0) {
      return <Box sx={{ width: "100%", height: 26 }} aria-hidden />;
    }

    return (
      <Box
        component="svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        sx={{ display: "block", width: "100%", height: 26 }}
        aria-hidden
      >
        {segments.map((points, index) => {
          const area = toOpsTatSparklineArea(points, height);
          const line = toOpsTatSparklinePath(points);
          return (
            <React.Fragment key={`spark-${index}`}>
              {area ? <path d={area} fill={color} opacity={0.08} /> : null}
              {points.length >= 2 ? (
                <path
                  d={line}
                  fill="none"
                  stroke={color}
                  strokeWidth={1.6}
                  vectorEffect="non-scaling-stroke"
                />
              ) : null}
              {points.length === 1 ? (
                <circle cx={points[0].x} cy={points[0].y} r={2} fill={color} />
              ) : null}
            </React.Fragment>
          );
        })}
        {lastPoint ? (
          <circle cx={lastPoint.x} cy={lastPoint.y} r={2} fill={color} />
        ) : null}
      </Box>
    );
  } catch (error) {
    return <Box sx={{ width: "100%", height: 26 }} aria-hidden />;
  }
};

const StageSlaBar = ({ stage }: { stage: OpsTatStageCard }) => {
  const theme = useTheme();
  const colors = getOpsTatStatusColors(theme, stage.status);
  const target = stage.target;
  const actual = stage.actual?.mean;

  let fillPct = 0;
  let markPct = (1 / SLA_BAR_CAP) * 100;

  try {
    if (target > 0 && actual != null && Number.isFinite(actual)) {
      const ratio = actual / target;
      fillPct = (Math.min(ratio, SLA_BAR_CAP) / SLA_BAR_CAP) * 100;
    }
    if (target > 0) {
      markPct = (1 / SLA_BAR_CAP) * 100;
    }
  } catch (error) {
    fillPct = 0;
  }

  return (
    <Box
      sx={{
        position: "relative",
        height: 4,
        borderRadius: "2px",
        bgcolor: theme.palette.customColors.veryLightGray,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: `${fillPct}%`,
          height: "100%",
          borderRadius: "2px",
          bgcolor: colors.fg,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: -2,
          left: `${markPct}%`,
          width: "1px",
          height: 8,
          bgcolor: theme.palette.customColors.navyText,
        }}
      />
    </Box>
  );
};

const StageCard = ({
  stage,
  onOpen,
}: {
  stage: OpsTatStageCard;
  onOpen: () => void;
}) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;
  const colors = getOpsTatStatusColors(theme, stage.status);

  const onKeyDown = (event: React.KeyboardEvent) => {
    try {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen();
      }
    } catch (error) {
      // ignore keyboard handler failures
    }
  };

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor:
          stage.applies_to === "custom"
            ? customColors.softCardBorder
            : "divider",
        borderStyle: stage.applies_to === "custom" ? "dashed" : "solid",
        cursor: "pointer",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: customColors.softCardBackground,
        "&:hover": {
          borderColor: customColors.purpleMain,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          color={customColors.greyTextColor}
        >
          {stage.code}
        </CustomTypography>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: colors.fg,
            flexShrink: 0,
          }}
        />
        <CustomTypography
          variant="heading8"
          color={customColors.navyText}
          title={stage.name}
        >
          {stage.name}
        </CustomTypography>
      </Box>

      {!stage.ready || !stage.actual ? (
        <CustomTypography variant="body2" color={customColors.greyTextColor}>
          Pending — not enough completed samples.
        </CustomTypography>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <CustomTypography
              variant="heading3"
              color={customColors.navyText}
              sx={{ letterSpacing: "-0.03em" }}
            >
              {stage.actual.mean.toFixed(1)}
            </CustomTypography>
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              d mean
            </CustomTypography>
            <Box
              sx={{
                ...getOpsTatStatusPillSx(theme, stage.status),
                ml: "auto",
              }}
            >
              <CustomTypography
                variant="caption"
                fontWeight={600}
                color={colors.fg}
              >
                {formatOpsTatSignedDays(stage.delta)}
              </CustomTypography>
            </Box>
          </Box>

          <StageSlaBar stage={stage} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              SLA {formatOpsTatDays(stage.target)}
            </CustomTypography>
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              Median {formatOpsTatDays(stage.actual.p50)}
            </CustomTypography>
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              P90 {formatOpsTatDays(stage.actual.p90)}
            </CustomTypography>
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              {formatOpsTatCount(stage.n)}
            </CustomTypography>
          </Box>
        </>
      )}

      <StageSparkline trend={stage.trend} color={colors.fg} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <CustomTypography
          variant="caption"
          color={customColors.greyTextColor}
          sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
        >
          {stage.owner}
        </CustomTypography>
        <CustomTypography variant="caption" color={customColors.greyTextColor}>
          8-week trend
        </CustomTypography>
      </Box>

      {stage.open !== undefined ? (
        <CustomTypography variant="caption" color={customColors.greyTextColor}>
          Open {formatOpsTatCount(stage.open)}
        </CustomTypography>
      ) : null}
    </Paper>
  );
};

const OpsTatStageGrid: React.FC<OpsTatStageGridProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const customColors = theme.palette.customColors;
    const stages = store.summaryResult?.data.stages ?? [];

    const openStage = async (stage: {
      stageId: string;
      name: string;
    }) => {
      try {
        await store.openDrawer(stage.stageId as OpsTatMetricId, {
          cardTitle: stage.name,
          cardSection: "stage_turnaround",
        });
      } catch (error) {
        // drilldownError is set on the store
      }
    };

    const renderBody = () => {
      if (store.summaryError && stages.length === 0) {
        return <OpsTatInlineState message={store.summaryError} />;
      }

      if (store.summaryLoading && stages.length === 0) {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="10rem"
          >
            <CircularProgress size={28} />
          </Box>
        );
      }

      if (stages.length === 0) {
        return (
          <OpsTatInlineState message="No stage turnaround data for the current filters." />
        );
      }

      return (
        <Grid2 container spacing={2} columns={12}>
          {stages.map((stage) => (
            <Grid2 key={stage.stageId} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <StageCard
                stage={stage}
                onOpen={() => {
                  void openStage(stage);
                }}
              />
            </Grid2>
          ))}
        </Grid2>
      );
    };

    return (
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          position: "relative",
        }}
      >
        {store.summaryLoading && stages.length > 0 ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.45)",
              zIndex: 1,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <Box>
            <CustomTypography variant="heading3" sx={{ mb: 0.5 }}>
              Stage turnaround
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={customColors.greyTextColor}
            >
              Mean days in stage, against SLA. The marker on each bar is the
              target.
            </CustomTypography>
          </Box>
          {stages.length > 0 ? (
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
              sx={{ letterSpacing: "0.06em", textTransform: "uppercase" }}
            >
              {stages.length} tracked handoffs
            </CustomTypography>
          ) : null}
        </Box>

        <OpsTatHowToRead
          section="stages"
          filters={store.appliedFilters}
          items={[
            "The large number is mean duration — how long the stage took.",
            "The signed pill and card colour are vs SLA on that mean. + = over SLA (slower). − = under SLA (faster).",
            "Median and P90 are shown for context. Do not add stage means to get the cycle total.",
          ]}
        />

        {renderBody()}
      </Paper>
    );
  },
);

export default OpsTatStageGrid;
